import { Suspense, lazy, type ReactNode } from 'react'
import { Navigate, Routes, Route } from 'react-router-dom'
import { Spin } from 'antd'
import { ProtectedRoute, GuestRoute, RoleBoundary } from '@shared'
import { useAuthStore } from '@features/auth'

const MainLayout = lazy(() => import('@shared/components/layout/MainLayout'))
const LoginPage = lazy(() => import('@features/auth/pages/LoginPage'))
const RegisterPage = lazy(() => import('@features/auth/pages/RegisterPage'))
const ProfilePage = lazy(() => import('@features/auth/pages/ProfilePage'))
const HomePage = lazy(() => import('@features/home/pages/HomePage'))
const NotFoundPage = lazy(() => import('@features/home/pages/NotFoundPage'))
const PhytosanitaryReportsPage = lazy(
  () => import('@features/phytosanitary/pages/PhytosanitaryReportsPage')
)
const InspectionTemplatesPage = lazy(
  () => import('@features/inspection-templates/pages/InspectionTemplatesPage')
)
const OrganizationsPage = lazy(() => import('@features/organizations/pages/OrganizationsPage'))
const AdminOverviewPage = lazy(() => import('@features/admin/pages/AdminOverviewPage'))
const OrganizationCardPage = lazy(() => import('@features/admin/pages/OrganizationCardPage'))
const AdminLayout = lazy(() => import('@shared/components/layout/AdminLayout'))
const AIUsagePage = lazy(() => import('@features/ai-usage/pages/AIUsagePage'))
const AdminNDVIPage = lazy(() => import('@features/admin/pages/AdminNDVIPage'))
const PlatformUsersPage = lazy(() => import('@features/platform-users/pages/PlatformUsersPage'))
const OrgUnitsPage = lazy(() => import('@features/org-units/pages/OrgUnitsPage'))
const UsersPage = lazy(() => import('@features/users/pages/UsersPage'))
const FieldsPage = lazy(() => import('@features/fields/pages/FieldsPage'))
const MapPage = lazy(() => import('@features/map/pages/MapPage'))
const TasksPage = lazy(() => import('@features/tasks/pages/TasksPage'))
const MyTasksPage = lazy(() => import('@features/tasks/pages/MyTasksPage'))
const AIAssistantPage = lazy(() => import('@features/ai-assistant/pages/AIAssistantPage'))
const ReferencesPage = lazy(() => import('@features/references/pages/ReferencesPage'))
const ImportReferencesPage = lazy(() => import('@features/references/pages/ImportReferencesPage'))
const CulturesPage = lazy(() => import('@features/references/pages/CulturesPage'))
const VarietiesPage = lazy(() => import('@features/references/pages/VarietiesPage'))
const PhenophasesPage = lazy(() => import('@features/references/pages/PhenophasesPage'))
const DiseasesPage = lazy(() => import('@features/references/pages/DiseasesPage'))
const PestsPage = lazy(() => import('@features/references/pages/PestsPage'))
const MeasurementUnitsPage = lazy(() => import('@features/references/pages/MeasurementUnitsPage'))
const MeasurementTypesPage = lazy(() => import('@features/references/pages/MeasurementTypesPage'))
const AccessGrantsPage = lazy(() => import('@features/access-grants/pages/AccessGrantsPage'))
const GrantedFarmsPage = lazy(() => import('@features/manufacturer/pages/GrantedFarmsPage'))
const ConsultationsPage = lazy(() => import('@features/consultations/pages/ConsultationsPage'))
const TreatmentsPage = lazy(() => import('@features/treatments/pages/TreatmentsPage'))
const ProductsPage = lazy(() => import('@features/references/pages/ProductsPage'))
const ActionLogPage = lazy(() => import('@features/audit/pages/ActionLogPage'))
const FieldTrialsPage = lazy(() => import('@features/field-trials/pages/FieldTrialsPage'))
const FieldTrialDetailPage = lazy(
  () => import('@features/field-trials/pages/FieldTrialDetailPageWrapper')
)
const WelcomePage = lazy(() => import('@features/demo/pages/WelcomePage'))
const DemoMapPage = lazy(() => import('@features/demo/pages/DemoMapPage'))

function RouteFallback() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Spin size="large" />
    </div>
  )
}

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>
}

function LegacyAdminUsersRedirect() {
  const { user, isLoading } = useAuthStore()
  if (isLoading) {
    return <RouteFallback />
  }
  if (user?.role === 'super_admin') {
    return <Navigate to="/admin/platform-users" replace />
  }
  return <Navigate to="/users" replace />
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<GuestRoute>{withSuspense(<LoginPage />)}</GuestRoute>} />
      <Route path="/register" element={<GuestRoute>{withSuspense(<RegisterPage />)}</GuestRoute>} />

      {/* Guest pages (document §2.1): outside ProtectedRoute, same for
          visitors and authorized users. */}
      <Route path="/welcome" element={withSuspense(<WelcomePage />)} />
      <Route path="/demo" element={withSuspense(<DemoMapPage />)} />
      <Route path="/admin/users" element={<LegacyAdminUsersRedirect />} />

      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredCapabilities={['manageOrganizations']}>
            <RoleBoundary>{withSuspense(<AdminLayout />)}</RoleBoundary>
          </ProtectedRoute>
        }
      >
        <Route index element={withSuspense(<AdminOverviewPage />)} />
        <Route path="organizations" element={withSuspense(<OrganizationsPage />)} />
        <Route path="organizations/:id" element={withSuspense(<OrganizationCardPage />)} />
        <Route path="platform-users" element={withSuspense(<PlatformUsersPage />)} />
        <Route path="ai-usage" element={withSuspense(<AIUsagePage />)} />
        <Route path="ndvi" element={withSuspense(<AdminNDVIPage />)} />
        <Route path="references" element={withSuspense(<ReferencesPage />)} />
        <Route path="references/import" element={withSuspense(<ImportReferencesPage />)} />
        <Route path="references/products" element={withSuspense(<ProductsPage />)} />
        <Route path="references/cultures" element={withSuspense(<CulturesPage />)} />
        <Route path="references/varieties" element={withSuspense(<VarietiesPage />)} />
        <Route path="references/phenophases" element={withSuspense(<PhenophasesPage />)} />
        <Route path="references/diseases" element={withSuspense(<DiseasesPage />)} />
        <Route path="references/pests" element={withSuspense(<PestsPage />)} />
        <Route
          path="references/measurement-units"
          element={withSuspense(<MeasurementUnitsPage />)}
        />
        <Route
          path="references/measurement-types"
          element={withSuspense(<MeasurementTypesPage />)}
        />
        <Route path="analytics" element={<Navigate to="/admin" replace />} />
        <Route path="org-units" element={<Navigate to="/org-units" replace />} />
      </Route>

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RoleBoundary>{withSuspense(<MainLayout />)}</RoleBoundary>
          </ProtectedRoute>
        }
      >
        <Route index element={withSuspense(<HomePage />)} />
        <Route path="profile" element={withSuspense(<ProfilePage />)} />
        <Route
          path="map"
          element={
            <ProtectedRoute requiredCapabilities={['viewFields']}>
              {withSuspense(<MapPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="fields"
          element={
            <ProtectedRoute requiredCapabilities={['viewFields']}>
              {withSuspense(<FieldsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute
              requiredCapabilities={['createReports', 'viewOrgReports', 'viewOwnReports']}
            >
              {withSuspense(<PhytosanitaryReportsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="treatments"
          element={
            <ProtectedRoute
              requiredCapabilities={['manageTreatments', 'completeTreatments', 'viewFields']}
            >
              {withSuspense(<TreatmentsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="field-trials"
          element={
            <ProtectedRoute requiredCapabilities={['manageFieldTrials', 'viewFieldTrials']}>
              {withSuspense(<FieldTrialsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="field-trials/:trialID"
          element={
            <ProtectedRoute requiredCapabilities={['manageFieldTrials', 'viewFieldTrials']}>
              {withSuspense(<FieldTrialDetailPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="reports/templates"
          element={
            <ProtectedRoute requiredCapabilities={['manageTemplates']}>
              {withSuspense(<InspectionTemplatesPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="tasks"
          element={
            <ProtectedRoute requiredCapabilities={['createTasks', 'viewOrgTasks']}>
              {withSuspense(<TasksPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="my-tasks"
          element={
            <ProtectedRoute requiredCapabilities={['viewAssignedTasks']}>
              {withSuspense(<MyTasksPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="ai-assistant"
          element={
            <ProtectedRoute requiredCapabilities={['useAIAssistant']}>
              {withSuspense(<AIAssistantPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="access-grants"
          element={
            <ProtectedRoute requiredCapabilities={['manageAccessGrants']}>
              {withSuspense(<AccessGrantsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="consultations"
          element={
            <ProtectedRoute
              requiredCapabilities={['manageConsultations', 'viewGrantedFarms', 'viewOrgReports']}
            >
              {withSuspense(<ConsultationsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="manufacturer/farms"
          element={
            <ProtectedRoute requiredCapabilities={['viewGrantedFarms']}>
              {withSuspense(<GrantedFarmsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="action-log"
          element={
            <ProtectedRoute requiredCapabilities={['viewActionLog']}>
              {withSuspense(<ActionLogPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="org-units"
          element={
            <ProtectedRoute requiredCapabilities={['manageOrgStructure']}>
              {withSuspense(<OrgUnitsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute requiredCapabilities={['manageUsers']}>
              {withSuspense(<UsersPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<ReferencesPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/import"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<ImportReferencesPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/products"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<ProductsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/cultures"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<CulturesPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/varieties"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<VarietiesPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/phenophases"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<PhenophasesPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/diseases"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<DiseasesPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/pests"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<PestsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/measurement-units"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<MeasurementUnitsPage />)}
            </ProtectedRoute>
          }
        />
        <Route
          path="references/measurement-types"
          element={
            <ProtectedRoute requiredCapabilities={['manageReferences']}>
              {withSuspense(<MeasurementTypesPage />)}
            </ProtectedRoute>
          }
        />
        <Route path="*" element={withSuspense(<NotFoundPage />)} />
      </Route>
    </Routes>
  )
}

export default AppRouter
